import React, { useCallback, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { CompanyListCompanyDto, CompanyScoringState, KosmosReportDto, VerificationStatusDto } from 'models/dtoTypes';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Tooltip from '@mui/material/Tooltip';
import {
  GetCompanyListItems,
  RescoreVerifiedCompanyList,
  ScoreVerifiedCompanyList,
  LinkCompany,
  GetEsgReportData, GetCompanyList,
} from 'services/companyList';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchCompanyDialog from './_searchCompanyDialog';
import { ICountryOption } from '.';
import Box from '@mui/material/Box';
import ExportKosmosReportsCsvLink from 'kosmos/components/ExportKosmosReportsCsvLink';

import { Apartment, Report, FactCheck, VerifiedUser, PrivacyTip } from '@mui/icons-material';
import ResourceTable, { IColumn, IResourceTableRef } from 'kosmos/components/ResourceTable';
import { IFilterField } from 'models/types';

const PREFIX = 'CompanyListVerificationTable';

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
  companyInfoCount: `${PREFIX}-companyInfoCount`,
  companyInfoVerified: `${PREFIX}-companyInfoVerified`,
  companyInfoSuccess: `${PREFIX}-companyInfoSuccess`,
  companyInfoError: `${PREFIX}-companyInfoError`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  [`& .${classes.companyInfoCount}`]: {
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
  },
  [`& .${classes.companyInfoVerified}`]: {
    color: theme.palette.success.dark,
    fontWeight: 'bold',
  },
  [`& .${classes.companyInfoSuccess}`]: {
    color: theme.palette.secondary.dark,
    fontWeight: 'bold',
  },
  [`& .${classes.companyInfoError}`]: {
    color: theme.palette.error.dark,
    fontWeight: 'bold',
  },
}));

interface IProps {
  countries: ICountryOption[];
  onErrorResponse: (e: unknown) => void;
  companyListId: string;
  columns: IColumn<CompanyListCompanyDto>[];
}

const filterSchema: IFilterField[] = [
  {
    label: 'Name',
    name: 'name',
    widget: 'text',
  },
  {
    label: 'External Identifier',
    name: 'externalIdentifier',
    widget: 'text',
  },
  {
    label: 'Verification Status',
    name: 'verificationStatus',
    widget: 'select',
    options: [
      { label: 'Pending', alias: VerificationStatusDto.Pending },
      { label: 'Not Found', alias: VerificationStatusDto.NotFound },
      { label: 'Verify Error', alias: VerificationStatusDto.VerifyError },
      { label: 'Multiple Results', alias: VerificationStatusDto.MultipleResults },
      { label: 'Verified', alias: VerificationStatusDto.Verified },
    ]
  },
  {
    label: 'Scoring Status',
    name: 'scoringState',
    widget: 'select',
    options: [
      { label: 'In Progress', alias: CompanyScoringState.InProgress },
      { label: 'Error', alias: CompanyScoringState.Error },
      { label: 'Completed', alias: CompanyScoringState.Completed },
    ]
  },
];

export default function CompanyListVerificationTable(props: IProps) {
  const {
    countries,
    columns,
    companyListId,
    onErrorResponse,
  } = props;

  const tableRef = useRef<IResourceTableRef>(null);

  const [selectedCompanyListItem, setSelectedCompanyListItem] = React.useState<CompanyListCompanyDto>();
  const [selectedCompanyNumber, setSelectedCompanyNumber] = React.useState<string>();
  const [scoringVerified, setScoringVerified] = React.useState<boolean>(false);
  const [rescoringVerified, setRescoringVerified] = React.useState<boolean>(false);

  const [companyData, setCompanyData] = React.useState({
    companyCount: 0,
    companyVerifiedCount: 0,
    companyScoredCount: 0,
    companyScoreErrorCount: 0,
  });

  const getCompanyData = useCallback(async (id: string) => {
    const { companyScoreErrorCount, companyScoredCount, companyVerifiedCount, companyCount } = await GetCompanyList(id);
    setCompanyData({
      companyScoreErrorCount: companyScoreErrorCount || 0, companyScoredCount: companyScoredCount || 0, companyVerifiedCount: companyVerifiedCount || 0, companyCount: companyCount || 0
    });
  }, [companyListId]);

  const isCompanyDataExist = () => {
    return Object.values(companyData).some((value) => value > 0);
  };

  React.useEffect(() => {
    getCompanyData(companyListId);
  }, [companyListId]);

  const canOpenSearchModal = (item: CompanyListCompanyDto) => {
    const { id, verificationStatus } = item;
    return Boolean(id && verificationStatus && ['NotFound', 'MultipleResults'].includes(verificationStatus));
  };

  const handleSelectRow = (item: CompanyListCompanyDto) => {
    const canBeSelected = canOpenSearchModal(item);
    if (canBeSelected) {
      setSelectedCompanyListItem(item);
      setSelectedCompanyNumber(item.registrationNumbers);
    }
  };


  const handleScoreVerified = async () => {
    setScoringVerified(true);
    try {
      await ScoreVerifiedCompanyList(companyListId);
    } catch (e) {
      onErrorResponse(e);
    }
    setScoringVerified(false);
  };

  const handleRescoreVerified = async () => {
    setRescoringVerified(true);
    try {
      await RescoreVerifiedCompanyList(companyListId);
    } catch (e) {
      onErrorResponse(e);
    }
    setRescoringVerified(false);
  };


  const handleRefresh = () => {
    tableRef.current?.loadResources(0);
  };

  const renderScoreVerifiedButton = () => {
    const verified = companyData.companyVerifiedCount;
    if (verified < 1) {
      const message = 'There is no verified companies to be scored';
      return (
        <Tooltip title={message}>
          <Button
            color="warning"
            variant="outlined"
            startIcon={<PlayCircleIcon />}>
            Score verified
          </Button>
        </Tooltip>
      );
    } else {
      return (
        <LoadingButton
          loading={scoringVerified}
          loadingPosition="start"
          color="info"
          onClick={handleScoreVerified}
          variant="contained"
          startIcon={<PlayCircleIcon />}>
          Score verified
        </LoadingButton>
      );
    }
  };

  const renderRescoreVerifiedButton = () => {
    const verified = companyData.companyVerifiedCount;
    if (verified < 1) {
      const message = 'There is no verified companies to be scored';
      return (
        <Tooltip title={message}>
          <Button
            color="warning"
            variant="outlined"
            startIcon={<PlayCircleIcon />}>
            Rescore
          </Button>
        </Tooltip>
      );
    } else {
      return (
        <LoadingButton
          loading={rescoringVerified}
          loadingPosition="start"
          color="info"
          onClick={handleRescoreVerified}
          variant="contained"
          startIcon={<PlayCircleIcon />}>
          Rescore
        </LoadingButton>
      );
    }
  };

  const renderTopButtons = () => {
    return (
      <Stack
        direction="row"
        spacing={2}
        justifyContent={isCompanyDataExist() ? 'space-between' : 'flex-end'}
        sx={{ mb: 2 }}
      >
        {isCompanyDataExist() && <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.companyInfoCount}
            title="Total Count"
          >
            <Apartment /> {companyData.companyCount}
          </Stack>
        </Stack>}
        {isCompanyDataExist() && <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.companyInfoVerified}
            title="Verification Complete"
          >
            <VerifiedUser /> {companyData.companyVerifiedCount}
          </Stack>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.companyInfoSuccess}
            title="Verification Pending"
          >
            <PrivacyTip /> {companyData.companyCount - companyData.companyVerifiedCount}
          </Stack>
        </Stack>}
        {isCompanyDataExist() && <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.companyInfoVerified}
            title="Scoring Completed"
          >
            <FactCheck /> {companyData.companyScoredCount}
          </Stack>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.companyInfoSuccess}
            title="Scoring In Progress"
          >
            <FactCheck /> {companyData.companyCount - companyData.companyScoredCount - companyData.companyScoreErrorCount}
          </Stack>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.companyInfoError}
            title="Scoring Failed"
          >
            <Report /> {companyData.companyScoreErrorCount}
          </Stack>
        </Stack>}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          {renderScoreVerifiedButton()}
          {renderRescoreVerifiedButton()}
          <Button
            color="primary"
            onClick={handleRefresh}
            variant="contained"
            startIcon={<RefreshIcon />}>
            Refresh
          </Button>
        </Stack>
      </Stack>
    );
  };

  const handleSelectMatchedCompany = async (companyListItemId: string, companyIds: string[]) => {
    try {
      await LinkCompany(companyListId, companyListItemId, companyIds);
      setSelectedCompanyListItem(undefined);
      tableRef.current?.loadResources(0);
    } catch (e) {
      onErrorResponse(e);
    }
  };

  const handleDownloadCsvReports = async (): Promise<KosmosReportDto[]> => {
    return await GetEsgReportData(companyListId);
  };

  return (
    <Root>
      {renderTopButtons()}
      <ResourceTable<CompanyListCompanyDto>
        ref={tableRef}
        stickyHeader={true}
        filterSchema={filterSchema}
        columns={columns}
        onFetch={(options) => GetCompanyListItems(companyListId, options)}
        isRowSelectable={canOpenSearchModal}
        onSelectRow={handleSelectRow}
        defaultSort={{
          sortColumn: 'name',
          isDesc: false
        }}
      />

      <SearchCompanyDialog
        countries={countries}
        selectedCompanyListItem={selectedCompanyListItem}
        selectedCompanyNumber={selectedCompanyNumber || ''}
        label="Search and select a company"
        onClose={() => setSelectedCompanyListItem(undefined)}
        onSelect={handleSelectMatchedCompany}
      />
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Box sx={{ flex: '1 1 auto' }} />
        <ExportKosmosReportsCsvLink
          fileName="output-esg-companyList.csv"
          onDownloadReports={handleDownloadCsvReports}
        />
      </Box>
    </Root>
  );
}
